import { EntityImage } from '@urbanx/agx-ui-components';
import {
  AgentDetails,
  CommunicationPreferences as CommunicationPreferencesModel,
} from '@urbanx/agx.agencies.client';
import { TeamMember } from '@urbanx/agx.agencies.client';
import {
  AgxCheckbox,
  AgxRow,
  AgxTable,
  AgxTypography,
  IColumnConfig,
} from '@urbanx/agx.ui-components2';
import { useCallback, useMemo } from 'react';

interface CommunicationPreferencesProps {
  members: TeamMember[];
  agents: AgentDetails[];
  onPreferencesChange: (members: TeamMember[]) => void;
}

const preferencesCols: (string | IColumnConfig)[] = [
  'Team Member',
  { name: 'Agreement', align: 'left' },
  { name: 'Listing', align: 'left' },
  { name: 'Contract', align: 'left' },
  { name: 'Commission', align: 'left' },
];

interface TeamMemberWithDetails extends TeamMember {
  photoUri?: string;
  fullName?: string;
}

const CommunicationPreferences = ({
  members,
  agents,
  onPreferencesChange,
}: CommunicationPreferencesProps) => {
  const membersWithDetails: TeamMemberWithDetails[] = useMemo(() => {
    return members.map((member) => {
      const agent = agents?.find((agent) => agent.id === member.agentId);
      return {
        ...member,
        photoUri: agent?.photo?.publicPath,
        fullName: agent ? `${agent.firstName} ${agent.lastName}` : undefined,
      };
    });
  }, [members, agents]);

  const updateCommunicationPreference = useCallback(
    (targetMember: TeamMemberWithDetails, preference: CommunicationPreferencesModel) => {
      const updatedMembers = membersWithDetails.map((member) => {
        if (member.agentId === targetMember.agentId) {
          return {
            ...member,
            communicationPreferences: {
              ...member.communicationPreferences,
              ...preference,
            },
          };
        }
        return member;
      });

      onPreferencesChange(updatedMembers);
    },
    [membersWithDetails, onPreferencesChange]
  );

  const rows = useMemo(() => {
    return membersWithDetails.map((member, index) => ({
      ['Team Member']: (
        <AgxRow gap="spacing-lg" alignItems="alignCenter">
          <EntityImage
            photoUri={agents.find((agent) => agent.id === member.agentId)?.photo?.publicPath}
            addRingToImage={true}
            name={member.fullName || ''}
          />
          <AgxTypography id={`txtMemberName-${index}`} type={'body'} size={'medium'}>
            {member.fullName}
          </AgxTypography>
        </AgxRow>
      ),
      ['Agreement']: (
        <AgxCheckbox
          id={`chkagreement-${index}`}
          checked={member.communicationPreferences?.agreement ?? false}
          onChange={(checked) =>
            updateCommunicationPreference(member, {
              agreement: checked,
            })
          }
          style="no text"
        />
      ),
      ['Listing']: (
        <AgxCheckbox
          id={`chklisting-${index}`}
          checked={member.communicationPreferences?.listing ?? false}
          onChange={(checked) =>
            updateCommunicationPreference(member, {
              listing: checked,
            })
          }
          style="no text"
        />
      ),
      ['Contract']: (
        <AgxCheckbox
          id={`chkcontract-${index}`}
          checked={member.communicationPreferences?.contract ?? false}
          onChange={(checked) =>
            updateCommunicationPreference(member, {
              contract: checked,
            })
          }
          style="no text"
        />
      ),
      ['Commission']: (
        <AgxCheckbox
          id={`chkcommission-${index}`}
          checked={member.communicationPreferences?.commission ?? false}
          onChange={(checked) =>
            updateCommunicationPreference(member, {
              commission: checked,
            })
          }
          style="no text"
        />
      ),
    }));
  }, [membersWithDetails, updateCommunicationPreference]);

  return <AgxTable columns={preferencesCols} rows={rows} variant="default" />;
};

export default CommunicationPreferences;
